import { ActivityIndicator } from "./activity-indicator"
import Grid from "@mui/material/Grid"
import Annoucement from "components/Announcement"
import InfiniteHorizontalScroller from "components/InfiniteHorizontalScroller"
import Profile from "components/Profile"
import ProfileHeader from "components/ProfileHeader"
import ProfileSidebar from "components/ProfileSidebar"
import SessionsListGrouped from "components/SessionsListGrouped"
import VideosList from "components/VideosList"
import AttachmentCard from "components/attachments/AttachmentCard"
import TeamsList from "components/team-members/TeamsList"
import useAuthRedirect from "hooks/useAuthRedirect"
import { api } from "lib/api"
import getConventionExhibitorWebLinks from "lib/getConventionExhibitorWebLinks"
import openWindowAsync from "lib/open-window-async"
import useCurrent from "lib/use-current"
import ConventionExhibitorProfileAttachment from "models/convention-api/v1/ConventionExhibitorProfileAttachment"
import ConventionProviderExhibitor from "models/convention-api/v1/ConventionProviderExhibitor"
import ConventionUserExhibitorFavorite from "models/convention-api/v1/ConventionUserExhibitorFavorite"
import { useRouter } from "next/router"
import pluralize from "pluralize"
import { useEffect, useState } from "react"
import HiddenBox from "components/HiddenBox"
import {pxToRem} from "../utils/helpers";
import { makeStyles } from "tss-react/mui-compat"

const useStyles = makeStyles()(
  (theme) => ({
    content: {
      backgroundColor: "rgba(216,216,216,0.1)",
      padding: "20px",
    },
    mobile: {
      padding: "20px",
    },
    sidebarDesktop: {
      width: '100%',
      [theme.breakpoints.up(900)]: {
        maxWidth: pxToRem(200),
      },
      [theme.breakpoints.up(1282)]: {
        maxWidth: pxToRem(350)
      }
    },
  })
)

interface IExhibitor {
  exhibitor: ConventionProviderExhibitor
}

const Exhibitor = (props: IExhibitor) => {
  const { exhibitor } = props

  const [webLink, setWebLink] = useState(null as any)
  const [favoriteExhibitorId, setFavoriteExhibitorId] = useState<null | string>(null)

  const authRedirect = useAuthRedirect()
  const { query } = useRouter()

  const { classes } = useStyles()

  const current = useCurrent()
  const user = current?.user

  const isLoggedIn = !!current?.user

  const attachments = ConventionExhibitorProfileAttachment.useMore({
    limit: 10,
    filter: {
      institution_id: exhibitor.institution_id,
    },
  })

  const { data: providerExhibitor } = ConventionProviderExhibitor.useOne(() => {
    return {
      filter: {
        disabled: "false",
        institution_id: exhibitor.institution_id,
      },
    }
  })

  const { data: favoriteExhibitor } = ConventionUserExhibitorFavorite.useOne(() => {
    if (!isLoggedIn) return null
    return {
      filter: {
        institution_id: exhibitor.institution_id,
      },
    }
  })

  useEffect(() => {
    if (!favoriteExhibitor?.id) return
    setFavoriteExhibitorId(favoriteExhibitor.id)
  }, [favoriteExhibitor?.id])

  useEffect(() => {
    const getExhibitorData = async () => {
      // wait till provider exhibitor loaded
      if (!providerExhibitor?.id) return

      const webLink = await getConventionExhibitorWebLinks(current, exhibitor.institution_id)
      setWebLink(webLink)
    }

    getExhibitorData()
  }, [providerExhibitor?.id])

  if (!exhibitor || webLink === null || !attachments.data) return <ActivityIndicator />

  const images = []

  const openAttachment = (attachment: ConventionExhibitorProfileAttachment) => () => {
    authRedirect(() =>
      openWindowAsync(async () => {
        if (user) {
          await api.createProfileAttachmentView({ req: undefined }, attachment.id, user.id)
        }
        return attachment.attachment_url || attachment.url || ""
      }),
    )
  }

  if (exhibitor.cover_photo_url) {
    // @ts-ignore
    images.push(exhibitor.cover_photo_url)
  }

  const onRightIconPress = () =>
    authRedirect(async () => {
      try {
        if (favoriteExhibitorId) {
          // @ts-ignore
          await api.removeUserFavoriteExhibitor({ req: undefined }, favoriteExhibitorId)
          setFavoriteExhibitorId(null)
          return
        }
        // @ts-ignore
        const newFavoriteExhibitorData = await api.addUserFavoriteExhibitor(
          { req: undefined },
          current?.user.id!,
          query.exhibitor_id as string,
        )
        setFavoriteExhibitorId(newFavoriteExhibitorData.data.id)
      } catch (error) {
        console.error(error)
      }
    })

  return (
    <>
      <ProfileHeader
        // @ts-ignore
        title={exhibitor.institution.name}
        // @ts-ignore
        subtitle={`${exhibitor.institution.city}, ${exhibitor.institution.region}`}
        images={images}
        socialLink={webLink}
        disabled={exhibitor.disabled}
        isFavorite={!!favoriteExhibitorId}
        onRightIconPress={onRightIconPress}
      />
      <HiddenBox variant="hiddenMDUp">
        <Grid container className={classes.mobile}>
          <Grid item xs={12}>
            <ProfileSidebar
              // @ts-ignore
              exhibitorName={exhibitor.institution.name}
              // @ts-ignore
              institutionId={exhibitor.institution.id}
              chatAvailable={exhibitor.institution?.chatAvailable?.status ?? "off"}
            />
          </Grid>
        </Grid>
      </HiddenBox>
      <Grid container className={classes.content}>
        <Grid item xs={12} md={9}>
          {exhibitor.announcement && <Annoucement announcement={exhibitor.announcement} />}
          {exhibitor.bio && (
            <Profile
              bio={exhibitor.bio}
              callToActionName={exhibitor.call_to_action_name}
              callToActionUrl={exhibitor.call_to_action_url}
              callToActionUrlLabel={`${exhibitor.call_to_action_name} will open in a new tab`}
            />
          )}
        </Grid>
        <HiddenBox variant="hiddenMDDown">
          <Grid item md={3} className={classes.sidebarDesktop}>
            <ProfileSidebar
              // @ts-ignore
              exhibitorName={exhibitor.institution.name}
              institutionId={exhibitor.institution_id}
              chatAvailable={exhibitor.institution?.chatAvailable?.status ?? "off"}
            />
          </Grid>
        </HiddenBox>
        <Grid item xs={12} mt={2}>
          <SessionsListGrouped
            institutionId={exhibitor.institution_id}
            exhibitorId={exhibitor.id}
          />
        </Grid>
        <Grid item xs={12}>
          <VideosList filter={{ institution_id: exhibitor.institution_id }} />
        </Grid>
        <Grid item xs={12}>
          <InfiniteHorizontalScroller
            title={`${pluralize("Attachment", attachments.data?.length)} (${
              attachments.data?.length
            })`}
            emptyStateMessage="No Attachments Found"
            hasMore={attachments.hasMore}
            loading={attachments.isLoading}
            loadMore={attachments.loadMore}>
            {attachments.data?.map((attachment) => {
              return (
                <AttachmentCard
                  key={attachment.id}
                  name={attachment.name}
                  description={attachment.description}
                  type={attachment.url ? "link" : "file"}
                  onClick={openAttachment(attachment)}
                />
              )
            })}
          </InfiniteHorizontalScroller>
        </Grid>
        <Grid item xs={12}>
          <TeamsList
            filter={{
              institution_id: exhibitor.institution_id,
            }}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default Exhibitor
